<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

const store = useStore();

// A single subject object ({ id, name }) — matches what vue-multiselect emits
// in single-select mode (null when nothing is selected).
const selectedSubject = ref(null);
const summary = ref('');

const subjects = useMapGetter(
  'conversationResolutionSubject/getConversationResolutionSubjects'
);
const uiFlags = useMapGetter('conversationResolutionSubject/getUIFlags');

const rules = computed(() => ({
  selectedSubject: { required },
}));

const v$ = useVuelidate(rules, { selectedSubject });

const hasSubjects = computed(() => subjects.value.length > 0);

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasSubjects.value
);

const submitState = reactive({ showLoading: false });

const fetchSubjects = async () => {
  try {
    await store.dispatch(
      'conversationResolutionSubject/getConversationResolutionSubject'
    );
  } catch (error) {
    // ignore
  }
};

onMounted(() => {
  if (!subjects.value.length) fetchSubjects();
});

const handleConfirm = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('confirm', {
    conversationResolutionSubjectId: selectedSubject.value?.id,
    summary: summary.value || null,
  });
};

const handleCancel = () => emit('cancel');
</script>

<template>
  <woot-modal :show="show" :on-close="handleCancel">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.TITLE')"
      />

      <div v-if="uiFlags.fetchingList" class="p-4 text-sm text-slate-500">
        {{ $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.LOADING') }}
      </div>

      <form
        v-else
        class="flex flex-col w-full"
        data-testid="resolution-form"
        @submit.prevent="handleConfirm"
      >
        <div v-if="!hasSubjects" class="p-4 text-sm text-red-500">
          {{
            $t(
              'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.EMPTY_SUBJECTS_HINT'
            )
          }}
        </div>

        <template v-else>
          <div class="w-full">
            <label :class="{ 'text-red-500': v$.selectedSubject.$error }">
              {{
                $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUBJECT_LABEL')
              }}
            </label>
            <multiselect
              v-model="selectedSubject"
              track-by="id"
              label="name"
              selected-label
              :options="subjects"
              :max-height="160"
              :placeholder="
                $t(
                  'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUBJECT_PLACEHOLDER'
                )
              "
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              data-testid="subject-multiselect"
              @close="v$.selectedSubject.$touch"
            >
              <template #noResult>
                {{
                  $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.NO_RESULTS')
                }}
              </template>
            </multiselect>
            <span v-if="v$.selectedSubject.$error" class="text-red-500 text-xs">
              {{
                $t(
                  'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUBJECT_REQUIRED'
                )
              }}
            </span>
          </div>

          <div class="w-full">
            <label>
              {{
                $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUMMARY_LABEL')
              }}
              <textarea
                v-model="summary"
                data-testid="summary-input"
                :rows="3"
                class="summary-textarea"
                :placeholder="
                  $t(
                    'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUMMARY_PLACEHOLDER'
                  )
                "
              />
            </label>
          </div>
        </template>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            type="submit"
            variant="solid"
            color="blue"
            :label="$t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.CONFIRM')"
            :disabled="isSubmitDisabled || isLoading"
            :is-loading="submitState.showLoading || isLoading"
            data-testid="confirm-resolution"
          />
          <button
            class="button clear"
            data-testid="cancel-resolution"
            @click.prevent="handleCancel"
          >
            {{ $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.CANCEL') }}
          </button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style scoped>
/* Override the global `textarea { height: 4rem }` base style — the scoped
   selector has higher specificity, so no !important is needed. */
.summary-textarea {
  height: 6rem;
  resize: vertical;
}
</style>
